"use client";

/** Renders action menus with optional content mounting for nested overlays. */

import { Menu as ArkMenu, type MenuRootProps } from "@ark-ui/react/menu";
import { Portal } from "@ark-ui/react/portal";
import { type IconComponent } from "@decibeltrade/react-icons";
import { menuItemFocusRing } from "@decibeltrade/web-core/recipes";
import { Fragment, JSX } from "react";
import { cva, cx } from "styled-system/css";

import { menu, menuItem, MenuVariants } from "@/recipes/menu";
import { Placement } from "@/types/ui";

export interface MenuItem {
  /** The ID of the menu item to be used as a unique key */
  id: string;
  /** The label to be displayed for the item in the menu */
  label: string | JSX.Element;
  /** A callback to be invoked when the menu item is selected */
  onSelect: () => void;
  /** An icon component to be rendered to the left of the menu item label */
  Icon?: IconComponent;
  /** If true, the menu item will not be selectable */
  disabled?: boolean;
  /** Whether the menu item should have a divider below it */
  hasDivider?: boolean;
}

export interface MenuProps {
  /**
   * The element that should open the menu when clicked.
   * The element should be an HTML `button` that can accept a ref.
   * You may need to wrap your component in React.forwardRef.
   */
  trigger: JSX.Element;
  /** The list of items that should be rendered in the menu */
  menuItems: MenuItem[];
  /** The size of the menu and its items */
  size?: MenuVariants["size"];
  /** Where the menu should be placed relative to the trigger element */
  placement?: Placement;
  /** Whether the menu is open */
  open?: boolean;
  /** Whether to render the menu in a portal attached to the document body */
  portalled?: boolean;
  /** Mount nested menu portals after their parent overlay. */
  mountOnlyWhenVisible?: boolean;
  /** Whether or not the menu should be dismissed after a menu item is selected */
  closeOnSelect?: boolean;
  /** Class name for applying additional styles to the menu */
  className?: string;
  /** The anchor point of the menu */
  anchorElement?: HTMLElement | null;
  /** TestId for the menu */
  testId?: string;
}

/** A component for opening a menu that appears adjacent to the trigger element provided. */
export function Menu({
  trigger,
  menuItems,
  anchorElement,
  size = "md",
  open,
  placement = "bottom-end",
  portalled = true,
  mountOnlyWhenVisible = false,
  closeOnSelect = true,
  className,
  testId,
}: MenuProps) {
  const openProps = open ? { open } : {};

  const onSelect: MenuRootProps["onSelect"] = (details) => {
    const selectedItem = menuItems.find((item) => item.id === details.value);
    selectedItem?.onSelect();
  };

  const menuContent = (
    <ArkMenu.Positioner>
      <ArkMenu.Content className={cx(menu({ size }), className)}>
        {menuItems.map(({ id, label, Icon, disabled, hasDivider }) => (
          <Fragment key={id}>
            <ArkMenu.Item
              value={id}
              disabled={disabled}
              className={cx(menuItem({ size }), menuItemFocusRing())}
            >
              {Icon && <Icon className={iconStyles({ size, disabled })} />}
              {label}
            </ArkMenu.Item>
            {hasDivider && <div className={dividerStyles({ size })} />}
          </Fragment>
        ))}
      </ArkMenu.Content>
    </ArkMenu.Positioner>
  );

  return (
    <ArkMenu.Root
      positioning={{
        placement,
        getAnchorRect: () => anchorElement?.getBoundingClientRect() ?? null,
      }}
      {...openProps}
      onSelect={onSelect}
      closeOnSelect={closeOnSelect}
      lazyMount={mountOnlyWhenVisible}
      unmountOnExit={mountOnlyWhenVisible}
    >
      <ArkMenu.Trigger data-testid={testId} asChild>
        {trigger}
      </ArkMenu.Trigger>
      {portalled ? <Portal>{menuContent}</Portal> : menuContent}
    </ArkMenu.Root>
  );
}

const iconStyles = cva({
  variants: {
    size: {
      xs: { h: "16", w: "16" },
      sm: { h: "16", w: "16" },
      md: { h: "16", w: "16" },
      lg: { h: "20", w: "20" },
    },
    disabled: { true: { color: "text.disabled!" } },
  },
});

const dividerStyles = cva({
  base: {
    display: "flex",
    bg: "background.tertiary",
    h: "1",
  },
  variants: {
    size: {
      xs: { mx: "4" },
      sm: { mx: "8" },
      md: { mx: "12" },
      lg: { mx: "16" },
    },
  },
  defaultVariants: { size: "md" },
});
